import {envIsPublicCloud} from '../../../../config';
import {UserAccountType} from '../../../../generated/graphql';
import {Viewer} from '../../../../state/viewer/types';
import {
  checkIsOrgServiceAccount,
  checkIsServiceAccount,
  checkIsTeamServiceAccount,
} from '../../../../util/serviceAccounts';
import {OrganizationMemberRole} from '../../../MembersTable/util';
import {
  OrganizationRow,
  QueryProjectMember,
  QueryProjectTeamMember,
  QueryRegistryOrganizationMember,
  RegistryMember,
  RegistryMemberType,
  RegistryRole,
  RegistryRoles,
  RegistryTeamMember,
} from './types';

function hasViewOnlyRegistryAccess(
  role: string,
  modelsSeat: string,
  accountType?: UserAccountType | null
): boolean {
  if (!envIsPublicCloud) {
    // if the user has a viewer account, they have view only registry access
    // otherwise, defer to models seat
    if (accountType === UserAccountType.Viewer) {
      return true;
    }
  }

  if (role === 'admin') {
    return false;
  }

  switch (modelsSeat) {
    case 'full':
      return false;
    case 'viewer':
      // models viewers have write access to registries
      return false;
    case 'none':
      return true;
    default:
      console.warn('Invalid models seat input');
      return true;
  }
}

export function getRoleFromString(
  role: string,
  enableRestrictedViewerRole: boolean = false
): RegistryRoles {
  switch (role) {
    case 'admin':
      return RegistryRoles.Admin;
    case 'member':
      return RegistryRoles.Member;
    case 'viewer':
      return RegistryRoles.Viewer;
    case 'restricted_viewer':
      return RegistryRoles.RestrictedViewer;
    default:
      console.warn('Invalid registry org role');
      return enableRestrictedViewerRole
        ? RegistryRoles.RestrictedViewer
        : RegistryRoles.Viewer;
  }
}

export function getStringFromRole(role: RegistryRoles): string {
  switch (role) {
    case RegistryRoles.Admin:
      return 'admin';
    case RegistryRoles.Member:
      return 'member';
    case RegistryRoles.Viewer:
      return 'viewer';
    case RegistryRoles.RestrictedViewer:
      return 'restricted_viewer';
    default:
      console.warn('Invalid registry role');
      return 'viewer';
  }
}

/**
 * Utility to convert project members to registry members
 * @param orgMembers
 * @param registryProjectMembers
 */
export const getRegistryMembers = (
  orgMembers: QueryRegistryOrganizationMember[],
  registryProjectMembers: QueryProjectMember[]
): RegistryMember[] => {
  const orgMembersMap = new Map<string, QueryRegistryOrganizationMember>();
  const registryProjectMembersMap = new Map<string, QueryProjectMember>();
  orgMembers.forEach(member => {
    orgMembersMap.set(member.user.id, member);
  });
  registryProjectMembers.forEach(member => {
    registryProjectMembersMap.set(member.id, member);
  });

  const registryMembers: RegistryMember[] = [];
  registryProjectMembersMap.forEach((member, memberId) => {
    const orgMember = orgMembersMap.get(memberId);
    if (orgMember?.role === OrganizationMemberRole.BILLING_ONLY) {
      return;
    }
    const viewOnlyRegistryAccess = checkIsServiceAccount(member.accountType)
      ? false
      : hasViewOnlyRegistryAccess(
          orgMember?.role ?? '',
          orgMember?.modelsSeat ?? '',
          orgMember?.user?.accountType
        );
    const registryMember: RegistryMember = {
      role: getRoleFromString(member.role.name),
      viewOnlyRegistryAccess,
      username: member.username ?? '',
      name: member.name ?? '',
      email: getRegistryUserEmail(
        member.accountType as UserAccountType,
        member.email
      ),
      id: memberId,
      photoUrl: member.photoUrl ?? '',
      accountType: member.accountType as UserAccountType,
      projectRoleIsInherited: member.projectRoleIsInherited ?? false,
      memberType: RegistryMemberType.User,
    };
    // With the new design updates, we don't want to individual rows for users
    // who have been assigned the default role. We only want to show the users
    // who have been explicitly added to the registry or whose roles have been
    // updated
    if (!registryMember.projectRoleIsInherited) {
      registryMembers.push(registryMember);
    }
  });
  return registryMembers;
};

export const getRegistryTeamMembers = (
  registryProjectTeamMembers: QueryProjectTeamMember[]
): RegistryTeamMember[] => {
  return registryProjectTeamMembers.map(member => ({
    role: getRoleFromString(member.role.name),
    id: member.team.id,
    name: member.team.name,
    photoUrl: member.team.photoUrl,
    numUsers: member.team.memberCount,
    memberType: RegistryMemberType.Team,
    teamMembers: member.team.members?.filter(m => m.id),
  }));
};

/**
 * With the addition of team members to registries,
 * a user (the viewer) can be a registry admin even through
 * one of their teams
 */
export const getIsViewerRegistryAdmin = (
  registryMembersMap: Map<string, RegistryMember>,
  registryTeamMembersMap: Map<string, RegistryTeamMember>,
  viewer: Viewer | undefined
): boolean => {
  if (registryMembersMap.get(viewer?.id ?? '')?.role === RegistryRoles.Admin) {
    return true;
  }
  if (registryTeamMembersMap.size === 0) {
    return false;
  }

  const viewerTeamIds =
    viewer?.teams?.edges?.map(teamEdge => teamEdge?.node?.id) ?? [];
  for (const teamId of viewerTeamIds) {
    if (registryTeamMembersMap.get(teamId)?.role === RegistryRoles.Admin) {
      return true;
    }
  }
  return false;
};

export const getRegistryMembersMap = <T extends {id: string}>(
  members: T[]
): Map<string, T> => {
  return new Map(members.map(member => [member.id, member]));
};

export const augmentRegistryMembers = (
  registryMembers: RegistryMember[],
  registryTeamMembers: RegistryTeamMember[],
  organizationMember?: OrganizationRow,
  enableRestrictedViewerRole: boolean = false
): RegistryMember[] => {
  const userTeamsMap: Record<string, (RegistryTeamMember | OrganizationRow)[]> =
    {};

  registryTeamMembers.forEach(team => {
    team.teamMembers?.forEach(user => {
      if (user?.id != null) {
        if (!userTeamsMap[user.id]) {
          userTeamsMap[user.id] = [];
        }
        userTeamsMap[user.id].push(team);
      }
    });
  });
  return registryMembers.map(member => {
    if (!userTeamsMap[member.id]) {
      userTeamsMap[member.id] = [];
    }
    if (organizationMember != null) {
      userTeamsMap[member.id]?.push(organizationMember);
    }
    const isUserOrgViewerOrServiceAccount =
      member.accountType === UserAccountType.OrgService ||
      member.viewOnlyRegistryAccess;
    return {
      ...member,
      teamRoles: userTeamsMap[member.id],
      augmentedRole: !isUserOrgViewerOrServiceAccount
        ? getAugmentedRole(
            member.role,
            userTeamsMap[member.id]?.map(m => m.role) ?? [],
            enableRestrictedViewerRole
          )
        : undefined,
    };
  });
};

const getRoleHierarchy = (
  enableRestrictedViewerRole: boolean = false
): RegistryRole[] => {
  const roleHierarchy: RegistryRole[] = [
    RegistryRoles.Viewer,
    RegistryRoles.Member,
    RegistryRoles.Admin,
  ];

  let defaultRole = RegistryRoles.Viewer;
  if (enableRestrictedViewerRole) {
    roleHierarchy.unshift(RegistryRoles.RestrictedViewer);
    defaultRole = RegistryRoles.RestrictedViewer;
  }
  return roleHierarchy;
};

const findRoleWithHighestHierarchy = (
  roles: RegistryRole[],
  roleHierarchy: RegistryRole[]
): RegistryRole => {
  const defaultRole = roleHierarchy[0];
  // Find the role with the highest privilege
  const highestRole = roles.reduce((currentHighest, role) => {
    return roleHierarchy.indexOf(role) > roleHierarchy.indexOf(currentHighest)
      ? role
      : currentHighest;
  }, defaultRole);
  return highestRole;
};

// Helper to find the most privileged role across all teams
// that a user is in
// an undefined augmented role implies no change in the role
// This helper wouldn't work with custom roles since there's no
// way to develop a hierarchy in that case.
export const getAugmentedRole = (
  userRegistryRole: RegistryRole,
  roles: RegistryRole[],
  enableRestrictedViewerRole: boolean = false
): RegistryRole | undefined => {
  const roleHierarchy = getRoleHierarchy(enableRestrictedViewerRole);
  const highestRole = findRoleWithHighestHierarchy(roles, roleHierarchy);
  return roleHierarchy.indexOf(highestRole) >
    roleHierarchy.indexOf(userRegistryRole)
    ? highestRole
    : undefined;
};

// Very similar to getAugmentedRole, but returns the final role
// of the user for the registry
export const getFinalRegistryRole = (
  userRegistryRole: RegistryRole,
  roles: RegistryRole[],
  enableRestrictedViewerRole: boolean = false
): RegistryRole | undefined => {
  const roleHierarchy = getRoleHierarchy(enableRestrictedViewerRole);
  if (userRegistryRole !== null) {
    roles.push(userRegistryRole);
  }
  return findRoleWithHighestHierarchy(roles, roleHierarchy);
};

export const getRegistryUserEmail = (
  accountType: UserAccountType,
  email?: string | null
): string => {
  if (checkIsOrgServiceAccount(accountType)) {
    return 'Service account (organization)';
  } else if (checkIsTeamServiceAccount(accountType)) {
    return 'Service account (team)';
  }
  if (email == null) {
    console.warn('User email is null');
    return '';
  }
  return email;
};
